import { toBool } from '../formatters';
import { toMergeCount, toMergeNumber, toTimeMs } from './numberUtils';

const ACTIVE_RUN_RETENTION_GRACE_MS = 20 * 1000;
const TERMINAL_ACTIVE_RUN_PHASES = new Set([
  'completed',
  'failed',
  'error',
  'terminated',
  'cancelled',
  'canceled',
  'finished',
  'done',
]);

const isTerminalActiveRunPhase = (phase) =>
  TERMINAL_ACTIVE_RUN_PHASES.has(String(phase || '').trim().toLowerCase());

const shouldRetainMissingActiveRun = (run, nowMs = Date.now()) => {
  if (!run || typeof run !== 'object') return false;
  if (isTerminalActiveRunPhase(run?.phase)) return false;
  const updatedAtMs = toTimeMs(run?.updated_at);
  if (updatedAtMs === null) return false;
  const ageMs = Math.max(0, nowMs - updatedAtMs);
  return ageMs <= ACTIVE_RUN_RETENTION_GRACE_MS;
};

export const normalizeRunConfig = (context = {}, fallback = {}, defaults = {}) => {
  const promptTemplate = context?.prompt_template || fallback?.prompt_template || '';
  const inferredEbayFromPrompt = String(promptTemplate).includes(
    'Additional context (retrieved from eBay image search):',
  );
  const inferredNotesFromPrompt = String(promptTemplate).includes(
    'Additional context (few-shot examples from reviewed test-rig notes):',
  );
  const inferredHiddenFromPrompt = String(promptTemplate).includes(
    'Hidden-item inference mode: OFF',
  )
    ? false
    : String(promptTemplate).includes('Hidden-item inference mode: ON')
      ? true
      : toBool(defaults.assumeHiddenItems, true);

  return {
    run_id: String(context?.run_id || fallback?.run_id || ''),
    pid: context?.pid ?? fallback?.pid ?? null,
    pgid: context?.pgid ?? fallback?.pgid ?? null,
    started_at: context?.started_at || fallback?.started_at || Date.now(),
    updated_at:
      context?.updated_at || context?.progress?.updated_at || fallback?.updated_at || null,
    model: context?.model || fallback?.model || defaults.modelName || '',
    resize_mode: context?.resize_mode || fallback?.resize_mode || defaults.resizeMode || '',
    compression_mode:
      context?.compression_mode || fallback?.compression_mode || defaults.compressionMode || '',
    dataset_prefix: context?.dataset_prefix || fallback?.dataset_prefix || '',
    dataset_name: context?.dataset_name || fallback?.dataset_name || defaults.datasetName || 'N/A',
    data_points:
      context?.data_points ??
      context?.total_data_points ??
      context?.progress?.total_data_points ??
      fallback?.data_points ??
      defaults.dataPoints ??
      '-',
    total_data_points:
      context?.total_data_points ??
      context?.progress?.total_data_points ??
      fallback?.total_data_points ??
      null,
    attempted_data_points:
      context?.attempted_data_points ??
      context?.progress?.attempted_data_points ??
      fallback?.attempted_data_points ??
      null,
    successful_data_points:
      context?.successful_data_points ??
      context?.progress?.successful_data_points ??
      fallback?.successful_data_points ??
      null,
    skipped_data_points:
      context?.skipped_data_points ??
      context?.progress?.skipped_data_points ??
      fallback?.skipped_data_points ??
      null,
    progress_percent:
      context?.progress_percent ?? context?.progress?.progress_percent ?? fallback?.progress_percent ?? null,
    avg_duration_seconds:
      context?.avg_duration_seconds ??
      context?.progress?.avg_duration_seconds ??
      fallback?.avg_duration_seconds ??
      null,
    eta_seconds: context?.eta_seconds ?? context?.progress?.eta_seconds ?? fallback?.eta_seconds ?? null,
    current_lot: context?.current_lot ?? context?.progress?.current_lot ?? fallback?.current_lot ?? null,
    current_index:
      context?.current_index ?? context?.progress?.current_index ?? fallback?.current_index ?? null,
    current_attempt:
      context?.current_attempt ??
      context?.progress?.current_attempt ??
      fallback?.current_attempt ??
      null,
    last_error: context?.last_error ?? context?.progress?.last_error ?? fallback?.last_error ?? '',
    dataset_truncated: toBool(context?.dataset_truncated, toBool(fallback?.dataset_truncated, false)),
    phase: context?.phase || context?.progress?.phase || fallback?.phase || 'running',
    prompt_template: promptTemplate,
    use_ebay_rag: toBool(
      context?.use_ebay_rag,
      toBool(fallback?.use_ebay_rag, inferredEbayFromPrompt || defaults.useEbayRag || false),
    ),
    use_notes_rag: toBool(
      context?.use_notes_rag,
      toBool(fallback?.use_notes_rag, inferredNotesFromPrompt || defaults.useNotesRag || false),
    ),
    assume_hidden_items: toBool(
      context?.assume_hidden_items,
      toBool(fallback?.assume_hidden_items, inferredHiddenFromPrompt),
    ),
  };
};

export const normalizeActiveRuns = (contexts = [], defaults = {}) => {
  const deduped = [];
  const seenKeys = new Set();

  contexts.forEach((ctx) => {
    if (!ctx || typeof ctx !== 'object') return;
    const normalized = normalizeRunConfig(ctx, {}, defaults);
    const key = normalized.run_id || String(normalized.pid || '');
    if (key && seenKeys.has(key)) return;
    if (key) seenKeys.add(key);
    deduped.push(normalized);
  });

  deduped.sort((a, b) => {
    const ta = new Date(a.started_at).getTime();
    const tb = new Date(b.started_at).getTime();
    const safeA = Number.isFinite(ta) ? ta : 0;
    const safeB = Number.isFinite(tb) ? tb : 0;
    return safeB - safeA;
  });

  return deduped;
};

export const mergeActiveRunForward = (previousRun, nextRun) => {
  if (!previousRun) return nextRun;
  if (!nextRun) return previousRun;

  const previousUpdatedAt = toTimeMs(previousRun?.updated_at);
  const nextUpdatedAt = toTimeMs(nextRun?.updated_at);
  const preferNextSnapshot =
    nextUpdatedAt !== null &&
    (previousUpdatedAt === null || nextUpdatedAt >= previousUpdatedAt);

  const merged = { ...previousRun, ...nextRun };
  const previousTotal = toMergeCount(previousRun?.total_data_points ?? previousRun?.data_points);
  const nextTotal = toMergeCount(nextRun?.total_data_points ?? nextRun?.data_points);
  const resolvedTotal =
    nextTotal !== null && nextTotal > 0
      ? nextTotal
      : previousTotal !== null && previousTotal > 0
        ? previousTotal
        : nextTotal;
  merged.total_data_points = resolvedTotal;
  if (resolvedTotal !== null) {
    merged.data_points = resolvedTotal;
  }

  [
    'attempted_data_points',
    'successful_data_points',
    'skipped_data_points',
    'current_index',
    'current_attempt',
  ].forEach((field) => {
    const previousValue = toMergeCount(previousRun?.[field]);
    const nextValue = toMergeCount(nextRun?.[field]);
    if (previousValue === null && nextValue === null) return;
    if (preferNextSnapshot && nextValue !== null) {
      merged[field] = nextValue;
      return;
    }
    if (nextValue === null) {
      merged[field] = previousValue;
      return;
    }
    if (previousValue === null) {
      merged[field] = nextValue;
      return;
    }
    merged[field] = Math.max(previousValue, nextValue);
  });

  const previousProgress = toMergeNumber(previousRun?.progress_percent);
  const nextProgress = toMergeNumber(nextRun?.progress_percent);
  if (previousProgress !== null || nextProgress !== null) {
    if (preferNextSnapshot && nextProgress !== null) {
      merged.progress_percent = nextProgress;
    } else if (nextProgress === null) {
      merged.progress_percent = previousProgress;
    } else if (previousProgress === null) {
      merged.progress_percent = nextProgress;
    } else {
      merged.progress_percent = Math.max(previousProgress, nextProgress);
    }
  }

  const previousEta = toMergeNumber(previousRun?.eta_seconds);
  const nextEta = toMergeNumber(nextRun?.eta_seconds);
  if (nextEta !== null || previousEta !== null) {
    merged.eta_seconds = nextEta !== null ? nextEta : previousEta;
  }

  const previousAvgDuration = toMergeNumber(previousRun?.avg_duration_seconds);
  const nextAvgDuration = toMergeNumber(nextRun?.avg_duration_seconds);
  if (nextAvgDuration !== null || previousAvgDuration !== null) {
    merged.avg_duration_seconds =
      nextAvgDuration !== null ? nextAvgDuration : previousAvgDuration;
  }

  if (previousUpdatedAt !== null && nextUpdatedAt !== null) {
    merged.updated_at =
      nextUpdatedAt >= previousUpdatedAt ? nextRun?.updated_at : previousRun?.updated_at;
  } else {
    merged.updated_at = nextRun?.updated_at || previousRun?.updated_at || null;
  }

  merged.started_at = nextRun?.started_at || previousRun?.started_at || Date.now();
  merged.phase = nextRun?.phase || previousRun?.phase || 'running';
  merged.last_error = nextRun?.last_error || previousRun?.last_error || '';
  return merged;
};

export const mergeActiveRunsForward = (previousRuns, nextRuns, defaults = {}) => {
  const nowMs = Date.now();
  const previousByKey = new Map();
  (previousRuns || []).forEach((run) => {
    const key = String(run?.run_id || run?.pid || '').trim();
    if (key) previousByKey.set(key, run);
  });

  const merged = (nextRuns || []).map((run) => {
    const key = String(run?.run_id || run?.pid || '').trim();
    if (!key) return run;
    return mergeActiveRunForward(previousByKey.get(key), run);
  });

  const nextKeys = new Set(
    (nextRuns || [])
      .map((run) => String(run?.run_id || run?.pid || '').trim())
      .filter(Boolean),
  );

  (previousRuns || []).forEach((run) => {
    const key = String(run?.run_id || run?.pid || '').trim();
    if (!key || nextKeys.has(key)) return;
    if (!shouldRetainMissingActiveRun(run, nowMs)) return;
    merged.push(run);
  });

  return normalizeActiveRuns(merged, defaults);
};
